import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { Stage, Layer } from 'react-konva'
import Konva from 'konva'
import {
  MagicTreeProps,
  TreeNodeData,
  TreeNodeId,
  FlattenedTreeNode,
  NodeDragStartEvent,
  NodeDragMoveEvent,
  NodeDragEndEvent,
} from '../types'
import { DEFAULT_NODE_HEIGHT, MIN_SCROLLBAR_THUMB_HEIGHT } from '../constants'
import { TreeNode } from './TreeNode'

// Helper to create a map of nodes by ID from the hierarchical data
const createNodeMap = <T extends Record<string, unknown>>(
  data: ReadonlyArray<TreeNodeData<T>>,
): Map<TreeNodeId, TreeNodeData<T>> => {
  const map = new Map<TreeNodeId, TreeNodeData<T>>()
  const stack = [...data]
  while (stack.length > 0) {
    const node = stack.pop()
    if (node) {
      map.set(node.id, node)
      if (node.children) {
        node.children.forEach((child) => stack.push(child))
      }
    }
  }
  return map
}

/**
 * Flattens the hierarchical tree data into a list, respecting expanded states.
 * @template T - The type of custom data in tree nodes.
 * @param {ReadonlyArray<TreeNodeData<T>>} data - The tree data.
 * @param {ReadonlySet<TreeNodeId>} expandedKeys - Set of expanded node IDs.
 * @param {number} nodeHeight - Height of each node.
 * @returns {ReadonlyArray<FlattenedTreeNode<T>>} - Flattened list of nodes with depth and y-position.
 */
const flattenTreeData = <T extends Record<string, unknown>>(
  data: ReadonlyArray<TreeNodeData<T>>,
  expandedKeysSet: ReadonlySet<TreeNodeId>,
  nodeHeight: number,
  parentId?: TreeNodeId,
  depth: number = 0,
  currentY: number = 0,
): FlattenedTreeNode<T>[] => {
  let flatList: FlattenedTreeNode<T>[] = []
  let runningY = currentY
  for (const node of data) {
    const isCurrentlyExpanded = expandedKeysSet.has(node.id)
    const flatNode: FlattenedTreeNode<T> = {
      ...(node as Omit<TreeNodeData<T>, 'isLoading'>),
      depth,
      y: runningY,
      parentId,
      isExpanded: isCurrentlyExpanded,
    }
    flatList.push(flatNode)
    runningY += nodeHeight
    if (node.children && isCurrentlyExpanded) {
      const childrenFlat = flattenTreeData(
        node.children,
        expandedKeysSet,
        nodeHeight,
        node.id,
        depth + 1,
        runningY,
      )
      flatList = flatList.concat(childrenFlat)
      if (childrenFlat.length > 0) {
        runningY = childrenFlat[childrenFlat.length - 1].y + nodeHeight
      }
    }
  }
  return flatList
}

/**
 * The main MagicTree component.
 * It uses Konva to render a tree structure, optimized for large datasets via virtualization.
 * @template T - The type of custom data associated with each tree node.
 */
export function MagicTree<T extends Record<string, unknown>>({
  data,
  expandedKeys,
  onExpand,
  nodeHeight = DEFAULT_NODE_HEIGHT,
  width: initialWidth,
  height: initialHeight,
  renderNodeContent,
  onNodeClick,
  onNodeHover,
  onLoadData,
  onNodeDragStart,
  icon,
  iconSize,
  showNodeTooltip,
}: MagicTreeProps<T>): React.ReactElement {
  const [scrollTop, setScrollTop] = useState<number>(0)
  const [hoveredNodeId, setHoveredNodeId] = useState<TreeNodeId | null>(null)
  const [loadingNodeIds, setLoadingNodeIds] = useState<Set<TreeNodeId>>(
    new Set(),
  )
  const [measuredWidth, setMeasuredWidth] = useState<number>(0)
  const [measuredHeight, setMeasuredHeight] = useState<number>(0)

  // State for internal tooltip management
  const [internalTooltipData, setInternalTooltipData] = useState<{
    visible: boolean
    content: React.ReactNode | string
    x: number
    y: number
    nodeId: TreeNodeId | null
  }>({
    visible: false,
    content: '',
    x: 0,
    y: 0,
    nodeId: null,
  })

  const stageRef = useRef<Konva.Stage>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const width = initialWidth ?? measuredWidth
  const height = initialHeight ?? measuredHeight

  const expandedKeysSet = useMemo(() => new Set(expandedKeys), [expandedKeys])

  useEffect(() => {
    if (initialWidth !== undefined && initialHeight !== undefined) {
      if (initialWidth !== undefined) setMeasuredWidth(initialWidth)
      if (initialHeight !== undefined) setMeasuredHeight(initialHeight)
      return
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries.length > 0) {
        const entry = entries[0]
        if (initialWidth === undefined) {
          setMeasuredWidth(entry.contentRect.width)
        }
        if (initialHeight === undefined) {
          setMeasuredHeight(entry.contentRect.height)
        }
      }
    })

    if (containerRef.current) {
      if (initialWidth === undefined) {
        setMeasuredWidth(containerRef.current.clientWidth)
      }
      if (initialHeight === undefined) {
        setMeasuredHeight(containerRef.current.clientHeight)
      }
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [initialWidth, initialHeight])

  const nodeMap = useMemo(() => createNodeMap(data), [data])

  const scrollbarDragData = useRef<{
    initialMouseY: number
    initialScrollTop: number
  } | null>(null)

  useEffect(() => {
    if (!onLoadData) return

    expandedKeys.forEach((nodeId) => {
      const nodeToLoad = nodeMap.get(nodeId)
      if (
        nodeToLoad &&
        nodeToLoad.hasAsyncChildren &&
        (!nodeToLoad.children || nodeToLoad.children.length === 0) &&
        !loadingNodeIds.has(nodeId)
      ) {
        setLoadingNodeIds((prev) => new Set(prev).add(nodeId))
        onLoadData(nodeToLoad)
          .catch((error) => {
            console.error(`Error loading data for node ${nodeId}:`, error)
          })
          .finally(() => {
            setLoadingNodeIds((prev) => {
              const newSet = new Set(prev)
              newSet.delete(nodeId)
              return newSet
            })
          })
      }
    })
  }, [expandedKeys, nodeMap, onLoadData, loadingNodeIds])

  const flattenedNodes = useMemo(() => {
    return flattenTreeData(data, expandedKeysSet, nodeHeight)
  }, [data, expandedKeysSet, nodeHeight])

  const totalContentHeight = useMemo(() => {
    return flattenedNodes.length * nodeHeight
  }, [flattenedNodes, nodeHeight])

  const visibleNodes = useMemo(() => {
    const startIndex = Math.floor(scrollTop / nodeHeight)
    const endIndex = Math.min(
      startIndex + Math.ceil(height / nodeHeight) + 1,
      flattenedNodes.length,
    )
    return flattenedNodes.slice(startIndex, endIndex)
  }, [flattenedNodes, scrollTop, height, nodeHeight])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault()
      const dy = e.evt.deltaY
      setScrollTop((prev) => {
        const newScrollTop = Math.max(
          0,
          Math.min(prev + dy, totalContentHeight - height),
        )
        return newScrollTop
      })
    }

    stage.on('wheel', handleWheel)
    return () => {
      stage.off('wheel', handleWheel)
    }
  }, [totalContentHeight, height])

  const handleNodePress = useCallback(
    (
      pressedNode: FlattenedTreeNode<T>,
      event: Konva.KonvaEventObject<MouseEvent>,
    ) => {
      if (onNodeDragStart && event.evt.button === 0) {
        const stage = stageRef.current
        if (!stage) return

        const pointerPosition = stage.getPointerPosition()
        if (!pointerPosition) return

        const originalNodeData = nodeMap.get(pressedNode.id)
        if (!originalNodeData) return

        const dragStartEvent: NodeDragStartEvent<T> = {
          node: originalNodeData,
          pointerPosition,
          rawEvent: event.evt as MouseEvent,
        }
        onNodeDragStart(dragStartEvent)

        event.evt.preventDefault()
      }
    },
    [onNodeDragStart, nodeMap],
  )

  const handleThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!scrollbarDragData.current) return

      const { initialMouseY, initialScrollTop } = scrollbarDragData.current
      const deltaY = e.clientY - initialMouseY

      const viewportHeight = height
      const scrollableDistTotal = totalContentHeight - viewportHeight
      if (scrollableDistTotal <= 0) return
      let thumbPixelHeight =
        (viewportHeight / totalContentHeight) * viewportHeight
      thumbPixelHeight = Math.max(thumbPixelHeight, MIN_SCROLLBAR_THUMB_HEIGHT)
      thumbPixelHeight = Math.min(thumbPixelHeight, viewportHeight)
      const scrollbarTrackEffectiveHeight = viewportHeight - thumbPixelHeight

      const scrollRatio = scrollableDistTotal / scrollbarTrackEffectiveHeight

      let newScrollTop = initialScrollTop + deltaY * scrollRatio
      newScrollTop = Math.max(0, Math.min(newScrollTop, scrollableDistTotal))
      setScrollTop(newScrollTop)
    },
    [height, totalContentHeight],
  )

  const handleThumbMouseUp = useCallback(() => {
    scrollbarDragData.current = null
    document.removeEventListener('mousemove', handleThumbMouseMove)
    document.removeEventListener('mouseup', handleThumbMouseUp)
  }, [handleThumbMouseMove])

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      scrollbarDragData.current = {
        initialMouseY: e.clientY,
        initialScrollTop: scrollTop,
      }
      document.addEventListener('mousemove', handleThumbMouseMove)
      document.addEventListener('mouseup', handleThumbMouseUp)
    },
    [scrollTop, handleThumbMouseMove, handleThumbMouseUp],
  )

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleThumbMouseMove)
      document.removeEventListener('mouseup', handleThumbMouseUp)
    }
  }, [handleThumbMouseMove, handleThumbMouseUp])

  const handleNodeHover = useCallback(
    (
      flattenedNode: FlattenedTreeNode<T> | null,
      event: Konva.KonvaEventObject<MouseEvent> | null,
    ) => {
      setHoveredNodeId(flattenedNode ? flattenedNode.id : null)

      const originalNode = flattenedNode ? nodeMap.get(flattenedNode.id) : null

      if (showNodeTooltip && originalNode && event) {
        let tooltipContent: React.ReactNode | string = ''
        if (typeof showNodeTooltip === 'function') {
          tooltipContent = showNodeTooltip(originalNode)
        } else if (showNodeTooltip === true) {
          tooltipContent = originalNode.label // Default to node label
        }

        if (tooltipContent) {
          setInternalTooltipData({
            visible: true,
            content: tooltipContent,
            x: event.evt.clientX + 10, // Offset tooltip slightly
            y: event.evt.clientY + 10,
            nodeId: originalNode.id,
          })
        } else {
          setInternalTooltipData((prev) => ({
            ...prev,
            visible: false,
            nodeId: null,
          }))
        }
      } else {
        setInternalTooltipData((prev) => ({
          ...prev,
          visible: false,
          nodeId: null,
        }))
      }

      if (onNodeHover) {
        onNodeHover(
          originalNode || (flattenedNode as unknown as TreeNodeData<T> | null), // Ensure type matches
          event,
        )
      }
    },
    [onNodeHover, nodeMap, showNodeTooltip, setInternalTooltipData], // Add dependencies
  )

  const handleNodeClick = useCallback(
    (
      flattenedNode: FlattenedTreeNode<T>,
      event: Konva.KonvaEventObject<MouseEvent>,
    ) => {
      if (onNodeClick) {
        const originalNode = nodeMap.get(flattenedNode.id)
        onNodeClick(
          originalNode || (flattenedNode as unknown as TreeNodeData<T>),
          event,
        )
      }
    },
    [onNodeClick, nodeMap],
  )

  const handleToggleExpand = useCallback(
    (nodeId: TreeNodeId) => {
      if (expandedKeysSet.has(nodeId)) {
        onExpand(expandedKeys.filter((id) => id !== nodeId))
      } else {
        onExpand([...expandedKeys, nodeId])
      }
    },
    [expandedKeys, expandedKeysSet, onExpand],
  )

  return (
    <div
      ref={containerRef}
      style={{
        width: initialWidth !== undefined ? `${initialWidth}px` : '100%',
        height: initialHeight !== undefined ? `${initialHeight}px` : '100%',
        overflow: 'hidden',
        position: 'relative', // Ensures fixed tooltip is relative to this container if not using portal
      }}
    >
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <Layer y={-scrollTop}>
          {visibleNodes.map((node) => (
            <TreeNode<T>
              key={node.id}
              node={node}
              x={0}
              y={node.y}
              width={width}
              height={nodeHeight}
              onToggleExpand={handleToggleExpand}
              renderNodeContent={renderNodeContent}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              isHovered={node.id === hoveredNodeId}
              isLoading={loadingNodeIds.has(node.id)}
              onNodePress={handleNodePress}
              icon={icon}
              iconSize={iconSize}
            />
          ))}
        </Layer>
      </Stage>
      {totalContentHeight > height && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '10px',
            height: `${height}px`,
            backgroundColor: '#eee',
          }}
        >
          {(() => {
            const viewportHeight = height
            const scrollableDist = totalContentHeight - viewportHeight
            let thumbPixelHeight =
              (viewportHeight / totalContentHeight) * viewportHeight
            thumbPixelHeight = Math.max(
              thumbPixelHeight,
              MIN_SCROLLBAR_THUMB_HEIGHT,
            )
            thumbPixelHeight = Math.min(thumbPixelHeight, viewportHeight)
            const scrollbarTrackEffectiveHeight =
              viewportHeight - thumbPixelHeight
            const thumbTop =
              scrollableDist > 0
                ? (scrollTop / scrollableDist) * scrollbarTrackEffectiveHeight
                : 0
            return (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: `${thumbTop}px`,
                  width: '100%',
                  height: `${thumbPixelHeight}px`,
                  backgroundColor: '#888',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
                onMouseDown={handleThumbMouseDown}
              />
            )
          })()}
        </div>
      )}
      {/* Render tooltip outside the Konva Stage, potentially using a Portal */}
      {internalTooltipData.visible &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: `${internalTooltipData.y}px`,
              left: `${internalTooltipData.x}px`,
              background: 'black',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              zIndex: 10000, // Ensure tooltip is on top
              pointerEvents: 'none', // Prevent tooltip from interfering with mouse events
              whiteSpace: 'nowrap',
            }}
          >
            {internalTooltipData.content}
          </div>,
          document.body, // Render tooltip at the end of the body
        )}
    </div>
  )
}
